/** Display formatters. */

export function formatCurrency(amount: number, currency = "MYR"): string {
  try {
    return new Intl.NumberFormat("en-MY", { style: "currency", currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-MY").format(value);
}

/** A 0..1 fraction as a whole-ish percentage, e.g. 0.124 → "12%". */
export function formatPercent(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** A signed change for a delta badge, e.g. 0.124 → "+12.4%"; null → "—". */
export function formatChange(fraction: number | null): string {
  if (fraction === null) return "—";
  const pct = fraction * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

/** Public path for a page: /<handle>/<slug>, or /p/<slug> for the default bucket. */
export function publicPagePath(handle: string | null | undefined, slug: string): string {
  return handle ? `/${handle}/${slug}` : `/p/${slug}`;
}

/** Number of masking stars, kept in a natural-looking 3..9 range. */
function stars(n: number): string {
  return "*".repeat(Math.min(Math.max(n, 3), 9));
}

/**
 * Partially mask a person's name for recording-safety ("Ahmad" → "A****").
 * Keeps the first character so it still reads as a real customer row. Falsy
 * values pass straight through so callers' `?? "—"` fallbacks still apply.
 */
export function maskName<T extends string | null | undefined>(value: T): T | string {
  if (!value) return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const first = Array.from(trimmed)[0];
  return `${first}${stars(trimmed.length - 1)}`;
}

/**
 * Partially mask an email ("ahmad@gmail.com" → "ah*****@gmail.com"). Keeps the
 * first 1–2 local chars and the real domain so it looks like a genuine address
 * on screen. Non-email strings fall back to {@link maskName}.
 */
export function maskEmail<T extends string | null | undefined>(value: T): T | string {
  if (!value) return value;
  const at = value.indexOf("@");
  if (at <= 0) return maskName(value);
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  const keep = local.length <= 2 ? 1 : 2;
  return `${local.slice(0, keep)}${stars(local.length - keep)}@${domain}`;
}

/**
 * Partially mask a phone number ("0123456789" → "01*****789"). Keeps the first
 * two and last three digits so a seller can still tell two rows apart, and
 * preserves the original punctuation positions so the column keeps its shape.
 */
export function maskPhone<T extends string | null | undefined>(value: T): T | string {
  if (!value) return value;
  const digits = value.replace(/\D/g, "");
  if (digits.length < 6) return maskName(value);
  const head = digits.slice(0, 2);
  const tail = digits.slice(-3);
  return `${head}${stars(digits.length - 5)}${tail}`;
}

/**
 * Mask a street address, keeping only the trailing locality words a seller uses
 * to eyeball a row ("12 Jalan Mawar, Taman Sri" → "*** Taman Sri"). The house
 * number and street — the parts that identify a household — are dropped rather
 * than starred, since star-count alone can still fingerprint an address.
 */
export function maskAddress<T extends string | null | undefined>(value: T): T | string {
  if (!value) return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const words = trimmed.split(/\s+/);
  if (words.length <= 2) return "***";
  return `*** ${words.slice(-2).join(" ")}`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-MY", { dateStyle: "medium" }).format(d);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-MY", { dateStyle: "medium", timeStyle: "short" }).format(d);
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return "—";
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
}
